import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { SystemIntegrationService } from 'brain-data/service/system-integration/system-integration.service';
import {
  createSystemIntegrationContact,
  createSystemIntegrationContactError,
  createSystemIntegrationContactSuccess,
  createSystemIntegrationLogin,
  createSystemIntegrationLoginError,
  createSystemIntegrationLoginSuccess,
  deleteSystemIntegrationContact,
  deleteSystemIntegrationContactError,
  deleteSystemIntegrationContactSuccess,
  deleteSystemIntegrationLogin,
  deleteSystemIntegrationLoginError,
  deleteSystemIntegrationLoginSuccess,
  loadSystemIntegrationContacts,
  loadSystemIntegrationContactsError,
  loadSystemIntegrationContactsSuccess,
  loadSystemIntegrationLoginPassword,
  loadSystemIntegrationLoginPasswordError,
  loadSystemIntegrationLoginPasswordSuccess,
  loadSystemIntegrationLogins,
  loadSystemIntegrationLoginsError,
  loadSystemIntegrationLoginsSuccess,
  updateSystemIntegrationContact,
  updateSystemIntegrationContactError,
  updateSystemIntegrationContactSuccess,
  updateSystemIntegrationLogin,
  updateSystemIntegrationLoginError,
  updateSystemIntegrationLoginSuccess,
} from '../actions/system-integration.actions';

@Injectable()
export class SystemIntegrationEffects {
  loadLogins$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadSystemIntegrationLogins),
      switchMap(() =>
        this.systemIntegrationService.getLogins().pipe(
          map(logins => loadSystemIntegrationLoginsSuccess({ logins })),
          catchError(error => of(loadSystemIntegrationLoginsError({ error }))),
        ),
      ),
    );
  });

  createLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createSystemIntegrationLogin),
      switchMap(({ payload }) =>
        this.systemIntegrationService.createLogin(payload).pipe(
          map(login => createSystemIntegrationLoginSuccess({ login })),
          catchError(error => of(createSystemIntegrationLoginError({ error }))),
        ),
      ),
    );
  });

  updateLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateSystemIntegrationLogin),
      switchMap(({ id, payload }) =>
        this.systemIntegrationService.updateLogin(id, payload).pipe(
          map(login => updateSystemIntegrationLoginSuccess({ login })),
          catchError(error => of(updateSystemIntegrationLoginError({ error }))),
        ),
      ),
    );
  });

  deleteLogin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteSystemIntegrationLogin),
      switchMap(({ id }) =>
        this.systemIntegrationService.deleteLogin(id).pipe(
          map(() => deleteSystemIntegrationLoginSuccess({ id })),
          catchError(error => of(deleteSystemIntegrationLoginError({ error }))),
        ),
      ),
    );
  });

  loadLoginPassword$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadSystemIntegrationLoginPassword),
      switchMap(({ id }) =>
        this.systemIntegrationService.getLoginPassword(id).pipe(
          map(password => loadSystemIntegrationLoginPasswordSuccess({ id, password })),
          catchError(error => of(loadSystemIntegrationLoginPasswordError({ error }))),
        ),
      ),
    );
  });

  loadContacts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadSystemIntegrationContacts),
      switchMap(({ loginId }) =>
        this.systemIntegrationService.getContacts(loginId).pipe(
          map(contacts => loadSystemIntegrationContactsSuccess({ loginId, contacts })),
          catchError(error => of(loadSystemIntegrationContactsError({ loginId, error }))),
        ),
      ),
    );
  });

  createContact$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createSystemIntegrationContact),
      switchMap(({ loginId, payload }) =>
        this.systemIntegrationService.createContact(loginId, payload).pipe(
          map(contact => createSystemIntegrationContactSuccess({ loginId, contact })),
          catchError(error => of(createSystemIntegrationContactError({ loginId, error }))),
        ),
      ),
    );
  });

  updateContact$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateSystemIntegrationContact),
      switchMap(({ loginId, id, payload }) =>
        this.systemIntegrationService.updateContact(loginId, id, payload).pipe(
          map(contact => updateSystemIntegrationContactSuccess({ loginId, contact })),
          catchError(error => of(updateSystemIntegrationContactError({ loginId, error }))),
        ),
      ),
    );
  });

  deleteContact$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteSystemIntegrationContact),
      switchMap(({ loginId, id }) =>
        this.systemIntegrationService.deleteContact(loginId, id).pipe(
          map(() => deleteSystemIntegrationContactSuccess({ loginId, id })),
          catchError(error => of(deleteSystemIntegrationContactError({ loginId, error }))),
        ),
      ),
    );
  });

  reloadLoginsOnSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createSystemIntegrationLoginSuccess, updateSystemIntegrationLoginSuccess, deleteSystemIntegrationLoginSuccess),
      map(() => loadSystemIntegrationLogins()),
    );
  });

  reloadContactsOnSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createSystemIntegrationContactSuccess, updateSystemIntegrationContactSuccess, deleteSystemIntegrationContactSuccess),
      map(({ loginId }) => loadSystemIntegrationContacts({ loginId })),
    );
  });

  constructor(
    private actions$: Actions,
    private systemIntegrationService: SystemIntegrationService,
  ) {}
}
